import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  DetailbeschreibungAiContext,
  schwachstellenService,
} from '../../services/schwachstellenService';

interface TranscriptEntry {
  id: string;
  speaker: 'user' | 'assistant' | 'system';
  text: string;
}

interface DetailbeschreibungAIAssistantDialogProps {
  open: boolean;
  context: DetailbeschreibungAiContext;
  existingContent: string;
  onClose: () => void;
  onApplyDraft: (html: string) => void;
}

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error';

const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

const DetailbeschreibungAIAssistantDialog: React.FC<DetailbeschreibungAIAssistantDialogProps> = ({
  open,
  context,
  existingContent,
  onClose,
  onApplyDraft,
}) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [transcriptEntries, setTranscriptEntries] = useState<TranscriptEntry[]>([]);
  const [draftHtml, setDraftHtml] = useState('');
  const [creatingDraft, setCreatingDraft] = useState(false);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const startedRef = useRef(false);
  const contextRef = useRef(context);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  const appendTranscript = useCallback((speaker: TranscriptEntry['speaker'], text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setTranscriptEntries((prev) => {
      const lastEntry = prev[prev.length - 1];
      if (lastEntry && lastEntry.speaker === speaker && lastEntry.text === trimmed) {
        return prev;
      }
      return [
        ...prev,
        {
          id: `${speaker}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          speaker,
          text: trimmed,
        },
      ];
    });
  }, []);

  const cleanupSession = useCallback(() => {
    startedRef.current = false;

    if (dataChannelRef.current) {
      dataChannelRef.current.onmessage = null;
      dataChannelRef.current.onopen = null;
      dataChannelRef.current.onclose = null;
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.ontrack = null;
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }
  }, []);

  const serializeTranscript = useMemo(() => (
    transcriptEntries
      .map((entry) => `${entry.speaker === 'user' ? 'Benutzer' : entry.speaker === 'assistant' ? 'AI-Assistant' : 'System'}: ${entry.text}`)
      .join('\n')
  ), [transcriptEntries]);

  const sendRealtimeEvent = useCallback((payload: unknown) => {
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') {
      throw new Error('Der Realtime-Datenkanal ist noch nicht bereit.');
    }
    channel.send(JSON.stringify(payload));
  }, []);

  const handleRealtimeEvent = useCallback((event: any) => {
    switch (event?.type) {
      case 'session.created':
      case 'session.updated':
        setConnectionState('connected');
        return;
      case 'conversation.item.input_audio_transcription.completed':
      case 'input_audio_transcription.completed':
        if (event.transcript) {
          appendTranscript('user', event.transcript);
        }
        return;
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
        if (event.transcript) {
          appendTranscript('assistant', event.transcript);
        }
        return;
      case 'response.output_text.done':
        if (event.text) {
          appendTranscript('assistant', event.text);
        }
        return;
      case 'error': {
        const detail = event.error?.message || 'Unbekannter Realtime-Fehler';
        setError(detail);
        setConnectionState('error');
        appendTranscript('system', `Fehler: ${detail}`);
        return;
      }
      default:
        return;
    }
  }, [appendTranscript]);

  const startSession = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;

    try {
      setError(null);
      setDraftHtml('');
      setTranscriptEntries([]);
      setConnectionState('connecting');

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Dieser Browser unterstuetzt kein Mikrofon-Streaming.');
      }

      const session = await schwachstellenService.createDetailbeschreibungRealtimeSession(contextRef.current);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      mediaStreamRef.current = mediaStream;

      const peerConnection = new RTCPeerConnection();
      peerConnectionRef.current = peerConnection;

      const audioElement = new Audio();
      audioElement.autoplay = true;
      audioElementRef.current = audioElement;

      peerConnection.ontrack = (trackEvent) => {
        audioElement.srcObject = trackEvent.streams[0];
      };

      mediaStream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, mediaStream);
      });

      const dataChannel = peerConnection.createDataChannel('oai-events');
      dataChannelRef.current = dataChannel;

      dataChannel.onmessage = (messageEvent) => {
        try {
          handleRealtimeEvent(JSON.parse(messageEvent.data));
        } catch {
          // Ignorieren wir - einzelne nicht-JSON Events sind fuer das UI nicht relevant.
        }
      };

      dataChannel.onopen = () => {
        appendTranscript('system', 'Realtime-Session verbunden. Der Assistant startet jetzt die Befragung.');
        setConnectionState('connected');
        sendRealtimeEvent({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: 'Starte jetzt das Interview fuer die Detailbeschreibung. Begruesse mich kurz und stelle sofort die wichtigste erste Rueckfrage.',
              },
            ],
          },
        });
        sendRealtimeEvent({ type: 'response.create' });
      };

      dataChannel.onclose = () => {
        appendTranscript('system', 'Realtime-Session wurde beendet.');
      };

      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.client_secret_value}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp || '',
      });

      if (!sdpResponse.ok) {
        const responseText = await sdpResponse.text();
        throw new Error(`OpenAI Realtime Verbindung fehlgeschlagen: ${responseText || sdpResponse.statusText}`);
      }

      const answerSdp = await sdpResponse.text();
      await peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });
    } catch (sessionError: any) {
      cleanupSession();
      setConnectionState('error');
      setError(sessionError?.message || 'Die Realtime-Session konnte nicht gestartet werden.');
    }
  }, [appendTranscript, cleanupSession, handleRealtimeEvent, sendRealtimeEvent]);

  const handleGenerateDraft = useCallback(async () => {
    if (!serializeTranscript.trim()) {
      setError('Es wurde noch kein Transkript erfasst. Fuehre zuerst ein kurzes Interview.');
      return;
    }

    try {
      setCreatingDraft(true);
      setError(null);
      const response = await schwachstellenService.generateDetailbeschreibungDraft(contextRef.current, serializeTranscript);
      setDraftHtml(response.html);
    } catch (draftError: any) {
      setError(draftError?.message || 'Der Entwurf konnte nicht generiert werden.');
    } finally {
      setCreatingDraft(false);
    }
  }, [serializeTranscript]);

  const handleApplyDraft = useCallback(() => {
    if (!draftHtml) return;
    if (existingContent.trim() && !window.confirm('Die bestehende Detailbeschreibung wird durch den KI-Entwurf ersetzt. Fortfahren?')) {
      return;
    }
    onApplyDraft(draftHtml);
    onClose();
  }, [draftHtml, existingContent, onApplyDraft, onClose]);

  const handleClose = useCallback(() => {
    cleanupSession();
    setConnectionState('idle');
    setError(null);
    setCreatingDraft(false);
    onClose();
  }, [cleanupSession, onClose]);

  useEffect(() => {
    if (open) {
      startSession();
      return;
    }

    cleanupSession();
    setConnectionState('idle');
    setError(null);
    setDraftHtml('');
    setTranscriptEntries([]);
    setCreatingDraft(false);
  }, [cleanupSession, open, startSession]);

  useEffect(() => () => {
    cleanupSession();
  }, [cleanupSession]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>AI-Assistant fuer Detailbeschreibung</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Der Assistant fuehrt ein kurzes Sprachinterview und sammelt gezielt die Fakten fuer einen ersten Entwurf der
            Detailbeschreibung.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              icon={connectionState === 'connected' ? <MicIcon /> : <MicOffIcon />}
              color={connectionState === 'connected' ? 'success' : connectionState === 'connecting' ? 'warning' : 'default'}
              label={
                connectionState === 'connected'
                  ? 'Mikrofon aktiv'
                  : connectionState === 'connecting'
                    ? 'Verbindung wird aufgebaut'
                    : connectionState === 'error'
                      ? 'Verbindung fehlgeschlagen'
                      : 'Nicht verbunden'
              }
            />
            <Chip icon={<AutoAwesomeIcon />} variant="outlined" label="Sprache: Deutsch" />
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Interview-Protokoll
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, minHeight: 220, maxHeight: 320, overflowY: 'auto' }}>
              {transcriptEntries.length === 0 ? (
                <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Stack spacing={1}>
                  {transcriptEntries.map((entry) => (
                    <Box key={entry.id}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                        {entry.speaker === 'user' ? 'Benutzer' : entry.speaker === 'assistant' ? 'AI-Assistant' : 'System'}
                      </Typography>
                      <Typography variant="body2">{entry.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Entwurf
              </Typography>
              <Button
                variant="contained"
                onClick={handleGenerateDraft}
                disabled={creatingDraft || connectionState === 'connecting'}
                startIcon={creatingDraft ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              >
                Entwurf generieren
              </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5, minHeight: 180, maxHeight: 320, overflowY: 'auto' }}>
              {draftHtml ? (
                <Box
                  sx={{
                    '& p': { mt: 0, mb: 1.25 },
                    '& ul, & ol': { pl: 3, mb: 1.25 },
                    '& li': { mb: 0.5 },
                    '& code': {
                      fontFamily: 'monospace',
                      bgcolor: 'action.hover',
                      px: 0.5,
                      borderRadius: 0.5,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: draftHtml }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nach dem Interview kannst du hier einen ersten HTML-Entwurf fuer die Detailbeschreibung erzeugen.
                </Typography>
              )}
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schliessen</Button>
        <Button variant="contained" onClick={handleApplyDraft} disabled={!draftHtml}>
          In Editor uebernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailbeschreibungAIAssistantDialog;
